// Stage 97: WARP — one-shot SVG sprite.
//
// Drops the brand's icon sprite into the document once. Use anywhere
// via <svg><use href="#icon-name"/></svg>. Mounting it multiple times
// is harmless — browsers de-duplicate <defs> with identical IDs by
// taking the first one encountered.
//
// Port of WARP-Zero-Design/ui_kits/widget/IconSprite.jsx — verbatim
// symbols, currentColor-bound strokes, no host-bound colors.

export function IconSprite(): JSX.Element {
  return (
    <svg
      width="0"
      height="0"
      style={{ position: 'absolute' }}
      aria-hidden="true"
    >
      <defs>
        {/* control icons */}
        <symbol
          id="icon-play"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="square"
        >
          <path d="M4 3 L13 8 L4 13 Z" />
        </symbol>
        <symbol
          id="icon-pause"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M5 3 V13 M11 3 V13" />
        </symbol>
        <symbol
          id="icon-reverse"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="square"
        >
          <path d="M2 8 L8 3 L8 13 Z" />
          <path d="M9 8 L14 8" />
        </symbol>
        <symbol
          id="icon-seed"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <circle cx="8" cy="8" r="1.5" fill="currentColor" />
          <path d="M8 2 V4 M8 12 V14 M2 8 H4 M12 8 H14 M3.5 3.5 L5 5 M11 11 L12.5 12.5 M3.5 12.5 L5 11 M11 5 L12.5 3.5" />
        </symbol>
        <symbol
          id="icon-cycle"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="square"
        >
          <path d="M2 8 A6 6 0 1 1 8 14" />
          <path d="M8 14 L5 11 M8 14 L11 11" />
        </symbol>
        <symbol
          id="icon-x"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="square"
        >
          <path d="M4 4 L12 12 M12 4 L4 12" />
        </symbol>
        <symbol
          id="icon-warning"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M8 1.5 L14.5 13.5 L1.5 13.5 Z" />
          <path d="M8 6 V9" strokeWidth="1.8" />
          <circle cx="8" cy="11" r="0.6" fill="currentColor" stroke="none" />
        </symbol>

        {/* kind icons — one per BITE kind */}
        <symbol
          id="icon-observation"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M1.5 8 C 3 4,6 3,8 3 C 10 3,13 4,14.5 8 C 13 12,10 13,8 13 C 6 13,3 12,1.5 8 Z" />
          <circle cx="8" cy="8" r="1.6" fill="currentColor" stroke="none" />
        </symbol>
        <symbol
          id="icon-question"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="square"
        >
          <path d="M5 5 Q5 3 8 3 Q11 3 11 5 Q11 7 8 8 L8 10" />
          <circle cx="8" cy="13" r="0.7" fill="currentColor" stroke="none" />
        </symbol>
        <symbol
          id="icon-objection"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="square"
        >
          <path d="M8 2 V10" strokeWidth="2" />
          <circle cx="8" cy="13" r="0.9" fill="currentColor" stroke="none" />
        </symbol>

        {/* swat tag icons — one per SwatTag */}
        <symbol
          id="icon-accept"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="square"
        >
          <path d="M3 8 L7 12 L13 4" />
        </symbol>
        <symbol
          id="icon-reject"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="square"
        >
          <path d="M4 4 L12 12 M12 4 L4 12" />
        </symbol>
        <symbol
          id="icon-defer"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <circle cx="8" cy="8" r="6" />
          <path d="M8 5 V8 L10 10" strokeLinecap="square" />
        </symbol>

        {/* layout icons */}
        <symbol
          id="icon-bite"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M5 2 C3 2,3 4,3 8 C 3 12,3 14,5 14" />
          <path d="M11 2 C13 2,13 4,13 8 C 13 12,13 14,11 14" />
          <circle cx="8" cy="8" r="1" fill="currentColor" stroke="none" />
        </symbol>
        <symbol
          id="icon-turn"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="square"
        >
          <path d="M3 6 H11 A2 2 0 0 1 13 8 V12 M11 12 L13 14 L11 12 Z" />
        </symbol>
        <symbol
          id="icon-glyph"
          viewBox="0 0 64 64"
          fill="none"
          stroke="currentColor"
          strokeLinecap="square"
        >
          <g transform="translate(32 32)" strokeWidth="2">
            <rect
              x="-22"
              y="-22"
              width="44"
              height="44"
              strokeOpacity="0.25"
            />
            <rect
              x="-16"
              y="-16"
              width="32"
              height="32"
              strokeOpacity="0.45"
            />
            <rect
              x="-10"
              y="-10"
              width="20"
              height="20"
              strokeOpacity="0.7"
            />
            <rect x="-5" y="-5" width="10" height="10" strokeOpacity="1" />
          </g>
          <circle cx="32" cy="32" r="2" fill="currentColor" stroke="none" />
        </symbol>
      </defs>
    </svg>
  );
}

interface IconProps {
  id: string;
  size?: number;
  style?: React.CSSProperties;
}

export function Icon({ id, size = 14, style }: IconProps): JSX.Element {
  return (
    <svg width={size} height={size} aria-hidden="true" style={style}>
      <use href={`#icon-${id}`} />
    </svg>
  );
}
